class SalutFaq extends HTMLElement {
  connectedCallback() {
    this.search = this.querySelector('.salut-faq__search');
    this.input = this.querySelector('[data-faq-input]');
    this.suggest = this.querySelector('[data-faq-suggest]');
    this.errorEl = this.querySelector('[data-faq-error]');
    this.browse = this.querySelector('[data-faq-browse]');
    this.results = this.querySelector('[data-faq-results]');
    this.topics = Array.from(this.querySelectorAll('[data-faq-topic]'));
    this.panels = Array.from(this.querySelectorAll('[data-faq-panel]'));
    if (!this.input || !this.browse) return;

    this.labels = this.readLabels();
    this.index = this.buildIndex();
    this.timer = null;

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.querySelector('[data-faq-submit]').addEventListener('click', () => this.commit());
    this.querySelector('[data-faq-clear]').addEventListener('click', () => this.reset(true));
    this.topics.forEach((button) => {
      button.addEventListener('click', () => this.selectTopic(button.dataset.faqTopic));
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeSuggest();
    });

    this.selectTopic(this.initialTopic(), false);
  }

  readLabels() {
    const holder = this.querySelector('[data-faq-labels]');
    if (!holder) return { no_results: '', back: '' };
    try {
      return JSON.parse(holder.textContent);
    } catch (error) {
      return { no_results: '', back: '' };
    }
  }

  buildIndex() {
    const entries = [];
    this.panels.forEach((panel) => {
      const topicId = panel.dataset.faqPanel;
      const topicTitle = panel.querySelector('.salut-faq__panel-title').textContent.trim();
      panel.querySelectorAll('.salut-faq__q').forEach((item) => {
        const question = item.querySelector('.salut-faq__q-title').textContent.trim();
        const answer = item.querySelector('.salut-faq__q-answer').textContent.trim();
        entries.push({
          topicId,
          topicTitle,
          question,
          answer,
          element: item,
          haystackQuestion: SalutFaq.normalize(question),
          haystackAnswer: SalutFaq.normalize(answer),
        });
      });
    });
    return entries;
  }

  static normalize(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  initialTopic() {
    const hash = decodeURIComponent(window.location.hash.replace('#faq-', ''));
    const match = this.panels.find((panel) => panel.dataset.faqPanel === hash);
    if (match) return match.dataset.faqPanel;
    return this.panels.length ? this.panels[0].dataset.faqPanel : null;
  }

  selectTopic(topicId, focusPanel = true) {
    if (!topicId) return;
    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.faqPanel === topicId);
    });
    this.topics.forEach((button) => {
      const active = button.dataset.faqTopic === topicId;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (active && focusPanel) {
        const details = button.closest('details');
        if (details) details.open = true;
      }
    });
    this.showBrowse();
  }

  onInput() {
    const value = this.input.value.trim();
    this.search.classList.toggle('is-filled', value.length > 0);
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.preview(value), 200);
  }

  onKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commit();
    }
    if (event.key === 'Escape') this.closeSuggest();
  }

  match(query) {
    const needle = SalutFaq.normalize(query);
    const inQuestion = this.index.filter((entry) => entry.haystackQuestion.includes(needle));
    const inAnswer = this.index.filter(
      (entry) => !entry.haystackQuestion.includes(needle) && entry.haystackAnswer.includes(needle)
    );
    return inQuestion.concat(inAnswer);
  }

  preview(query) {
    if (query.length < 2) {
      this.closeSuggest();
      this.setError(false);
      return;
    }
    const matches = this.match(query);
    if (!matches.length) {
      this.closeSuggest();
      this.setError(true);
      return;
    }
    this.setError(false);
    this.renderSuggest(matches.slice(0, 6), query);
  }

  renderSuggest(matches, query) {
    this.suggest.textContent = '';
    matches.forEach((entry) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'salut-faq__suggestion';

      const head = document.createElement('span');
      head.className = 'salut-faq__suggestion-head';

      const title = document.createElement('span');
      title.className = 'salut-faq__suggestion-title';
      SalutFaq.highlight(title, entry.question, query);
      head.appendChild(title);

      const chevron = this.querySelector('.salut-faq__q .salut-faq__chevron');
      if (chevron) {
        const copy = chevron.cloneNode(true);
        copy.style.transform = 'rotate(0deg)';
        head.appendChild(copy);
      }

      const answer = document.createElement('span');
      answer.className = 'salut-faq__suggestion-answer';
      SalutFaq.highlight(answer, entry.answer, query);

      button.appendChild(head);
      button.appendChild(answer);
      button.addEventListener('click', () => this.jumpTo(entry));
      this.suggest.appendChild(button);
    });
    this.suggest.classList.add('is-open');
  }

  static highlight(target, text, query) {
    const haystack = SalutFaq.normalize(text);
    const needle = SalutFaq.normalize(query);
    let cursor = 0;
    let found = haystack.indexOf(needle, cursor);
    while (found !== -1) {
      target.appendChild(document.createTextNode(text.slice(cursor, found)));
      const mark = document.createElement('mark');
      mark.textContent = text.slice(found, found + needle.length);
      target.appendChild(mark);
      cursor = found + needle.length;
      found = haystack.indexOf(needle, cursor);
    }
    target.appendChild(document.createTextNode(text.slice(cursor)));
  }

  jumpTo(entry) {
    this.closeSuggest();
    this.selectTopic(entry.topicId);
    entry.element.open = true;
    entry.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  commit() {
    const query = this.input.value.trim();
    this.closeSuggest();
    if (query.length < 2) {
      this.reset(false);
      return;
    }
    const matches = this.match(query);
    if (!matches.length) {
      this.setError(true);
      this.showBrowse();
      return;
    }
    this.setError(false);
    this.renderResults(matches);
  }

  renderResults(matches) {
    this.results.textContent = '';
    const groups = new Map();
    matches.forEach((entry) => {
      if (!groups.has(entry.topicId)) groups.set(entry.topicId, { title: entry.topicTitle, entries: [] });
      groups.get(entry.topicId).entries.push(entry);
    });

    groups.forEach((group) => {
      const heading = document.createElement('h2');
      heading.className = 'salut-faq__panel-title';
      heading.textContent = group.title;
      this.results.appendChild(heading);
      group.entries.forEach((entry) => {
        const copy = entry.element.cloneNode(true);
        copy.open = true;
        const chevron = copy.querySelector('.salut-faq__chevron');
        if (chevron) chevron.style.transform = 'rotate(0deg)';
        this.results.appendChild(copy);
      });
    });

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'salut-faq__back';
    back.textContent = this.labels.back;
    back.addEventListener('click', () => this.reset(true));
    this.results.appendChild(back);

    this.browse.classList.add('is-hidden');
    this.results.classList.add('is-active');
  }

  showBrowse() {
    this.results.classList.remove('is-active');
    this.results.textContent = '';
    this.browse.classList.remove('is-hidden');
  }

  setError(state) {
    this.errorEl.textContent = state ? this.labels.no_results : '';
    this.search.classList.toggle('is-error', state);
  }

  closeSuggest() {
    this.suggest.classList.remove('is-open');
    this.suggest.textContent = '';
  }

  reset(clearInput) {
    if (clearInput) {
      this.input.value = '';
      this.search.classList.remove('is-filled');
    }
    this.closeSuggest();
    this.setError(false);
    this.showBrowse();
  }
}

if (!customElements.get('salut-faq')) {
  customElements.define('salut-faq', SalutFaq);
}
